import {
  type DropTargetDropEvent,
  type DropTargetOptions,
  IconButton,
  toast,
  useDropTarget,
} from '@affine/component';
import { usePageHelper } from '@affine/core/blocksuite/block-suite-page-list/utils';
import { SIDEBAR_ACTION_REVEAL_KEEP_ACTIVE_ATTR } from '@affine/core/modules/app-sidebar/views';
import { buildSidebarCreateActions } from '@affine/core/modules/app-sidebar/views/area-actions/create-actions';
import { AccessibleDocCacheStore } from '@affine/core/modules/doc/stores/accessible-doc-cache';
import type { FavoriteSupportTypeUnion } from '@affine/core/modules/favorite';
import {
  FavoriteService,
  isFavoriteSupportType,
} from '@affine/core/modules/favorite';
import { NavigationPanelService } from '@affine/core/modules/navigation-panel';
import { AccessibleFolderCacheStore } from '@affine/core/modules/organize';
import {
  useCollectionsFeatureEnabled,
  useTagsFeatureEnabled,
} from '@affine/core/modules/streamlain';
import { WorkspaceService } from '@affine/core/modules/workspace';
import type { AffineDNDData } from '@affine/core/types/dnd';
import { useI18n } from '@affine/i18n';
import { track } from '@affine/track';
import { streamLainAnimationController } from '@blocksuite/affine-shared/utils';
import { FavoritedIcon } from '@blocksuite/icons/rc';
import {
  LiveData,
  useLiveData,
  useService,
  useServices,
} from '@toeverything/infra';
import clsx from 'clsx';
import { type RefObject, useCallback, useMemo, useRef } from 'react';

import { CollapsibleSection } from '../../layouts/collapsible-section';
import { NavigationPanelCollectionNode } from '../../nodes/collection';
import { NavigationPanelDocNode } from '../../nodes/doc';
import { NavigationPanelFolderNode } from '../../nodes/folder';
import { NavigationPanelTagNode } from '../../nodes/tag';
import {
  DropEffect,
  type NavigationPanelTreeNodeDropEffect,
  NavigationPanelTreeRoot,
} from '../../tree';
import { NavigationTreeSectionOwner } from '../../tree/navigation';
import { useNavigationTreeEntries } from '../../tree/navigation-model';
import { SidebarAreaHeaderActions } from '../../tree/sidebar-area-header-actions';
import type { NodeOperation } from '../../tree/types';
import {
  compareSidebarManualOrderItems,
  sortSidebarItems,
  useSidebarSortMode,
} from '../../tree/use-sidebar-manual-order';
import {
  favoriteChildrenCanDrop,
  favoriteChildrenDropEffect,
  favoriteRootCanDrop,
  favoriteRootDropEffect,
} from './dnd';
import { RootEmpty } from './empty';
import * as panelStyles from './panel.css';
import * as pendingRemovalStyles from './pending-removal-star.css';

const KEEP_ACTION_REVEAL_ACTIVE_PROPS = {
  [SIDEBAR_ACTION_REVEAL_KEEP_ACTIVE_ATTR]: 'true',
} as const;

export const NavigationPanelFavorites = ({
  presentation = 'section',
  revealOnExpand = false,
  navigationControl,
}: {
  presentation?: 'panel' | 'section';
  revealOnExpand?: boolean;
  navigationControl?: {
    /** The control that shows the favorites, or an element around it. */
    triggerRef: RefObject<HTMLElement | null>;
    /** Finds the control inside `triggerRef`. */
    triggerSelector?: string;
    open: boolean;
    onOpen: () => void;
  };
} = {}) => {
  const accessibleFolderCache = useService(AccessibleFolderCacheStore);
  const accessibleDocCache = useService(AccessibleDocCacheStore);
  const collectionsEnabled = useCollectionsFeatureEnabled();
  const tagsEnabled = useTagsFeatureEnabled();
  const { favoriteService, workspaceService, navigationPanelService } =
    useServices({
      FavoriteService,
      WorkspaceService,
      NavigationPanelService,
    });

  const path = useMemo(() => ['favorites'], []);

  const favorites = useLiveData(favoriteService.favoriteList.sortedList$);
  const visibleFavorites = useMemo(
    () =>
      favorites.filter(favorite => {
        if (favorite.type === 'collection') {
          return collectionsEnabled;
        }
        if (favorite.type === 'tag') {
          return tagsEnabled;
        }
        return true;
      }),
    [collectionsEnabled, favorites, tagsEnabled]
  );

  const accessibleFolders$ = useMemo(
    () =>
      LiveData.from(
        accessibleFolderCache.watchAllAccessibleFolders(),
        accessibleFolderCache.getCachedFolders()
      ),
    [accessibleFolderCache]
  );
  const accessibleFolders = useLiveData(accessibleFolders$);
  const folderNameById = useMemo(
    () => new Map(accessibleFolders.map(folder => [folder.id, folder.name])),
    [accessibleFolders]
  );
  const accessibleDocs$ = useMemo(
    () =>
      LiveData.from(
        accessibleDocCache.watchAllAccessibleDocs(),
        accessibleDocCache.getCachedDocs()
      ),
    [accessibleDocCache]
  );
  const accessibleDocs = useLiveData(accessibleDocs$);
  const docTitleById = useMemo(
    () => new Map(accessibleDocs.map(doc => [doc.id, doc.title])),
    [accessibleDocs]
  );
  const favoriteSortStorageKey = useMemo(
    () =>
      `streamlain:favorites-sidebar-sort-mode:${workspaceService.workspace.id}`,
    [workspaceService.workspace.id]
  );
  const { setSortMode: setFavoriteSortMode, sortMode: favoriteSortMode } =
    useSidebarSortMode(favoriteSortStorageKey, 'manual');
  const favoriteItems = useMemo(
    () =>
      visibleFavorites.map(favorite => ({
        favorite,
        key: `${favorite.type}:${favorite.id}`,
        label:
          favorite.type === 'doc'
            ? (docTitleById.get(favorite.id) ?? '')
            : favorite.type === 'folder'
              ? (folderNameById.get(favorite.id) ?? '')
              : favorite.id,
        type: favorite.type,
      })),
    [docTitleById, folderNameById, visibleFavorites]
  );
  const visibleFavoriteItems = useMemo(() => {
    if (favoriteSortMode === 'manual') {
      return favoriteItems;
    }
    return sortSidebarItems(
      favoriteItems,
      favoriteSortMode,
      compareSidebarManualOrderItems
    );
  }, [favoriteItems, favoriteSortMode]);
  const navigationRoots = useMemo(
    () =>
      visibleFavoriteItems.map(item => ({
        kind: item.favorite.type,
        id: item.favorite.id,
        label: item.label,
      })),
    [visibleFavoriteItems]
  );
  const navigationEntries = useNavigationTreeEntries(navigationRoots, path);
  const privateRootFolderId = useMemo(() => {
    if (workspaceService.workspace.flavour === 'local') {
      return null;
    }
    return (
      accessibleFolders.find(folder => folder.systemKind === 'private_root')
        ?.id ?? null
    );
  }, [accessibleFolders, workspaceService.workspace.flavour]);
  const resolvePrivateRootFolderId = useCallback(async () => {
    if (workspaceService.workspace.flavour === 'local') {
      return null;
    }
    if (privateRootFolderId) {
      return privateRootFolderId;
    }
    await accessibleFolderCache.fetchAccessibleFolders().catch(() => {});
    return (
      accessibleFolderCache
        .getCachedFolders()
        .find(folder => folder.systemKind === 'private_root')?.id ?? null
    );
  }, [
    accessibleFolderCache,
    privateRootFolderId,
    workspaceService.workspace.flavour,
  ]);

  const isLoading = useLiveData(favoriteService.favoriteList.isLoading$);

  const t = useI18n();

  const { createPage } = usePageHelper();

  const handleDrop = useCallback(
    (_data: DropTargetDropEvent<AffineDNDData>) => {
      return;
    },
    []
  );

  const handleCreateNewFavoriteDoc = useCallback(() => {
    void resolvePrivateRootFolderId().then(targetFolderId => {
      if (workspaceService.workspace.flavour !== 'local' && !targetFolderId) {
        toast(t['com.streamlain.rootAppSidebar.privateFolder.createFailed']());
        return;
      }
      const newDoc = createPage(undefined, {
        at: 'active',
        targetFolderId: targetFolderId ?? undefined,
      });
      favoriteService.favoriteList.add(
        'doc',
        newDoc.id,
        favoriteService.favoriteList.indexAt('before')
      );
      setFavoriteSortMode('manual');
      navigationPanelService.setCollapsed(path, false);
    });
  }, [
    createPage,
    navigationPanelService,
    favoriteService.favoriteList,
    path,
    resolvePrivateRootFolderId,
    setFavoriteSortMode,
    t,
    workspaceService.workspace.flavour,
  ]);

  const handleOnChildrenDrop = useCallback(
    (
      favorite: { id: string; type: FavoriteSupportTypeUnion },
      data: DropTargetDropEvent<AffineDNDData>
    ) => {
      if (
        data.treeInstruction?.type === 'reorder-above' ||
        data.treeInstruction?.type === 'reorder-below'
      ) {
        if (
          data.source.data.from?.at === 'navigation-panel:favorite:list' &&
          data.source.data.entity?.type &&
          isFavoriteSupportType(data.source.data.entity.type)
        ) {
          setFavoriteSortMode('manual');
          // is reordering
          favoriteService.favoriteList.reorder(
            data.source.data.entity.type,
            data.source.data.entity.id,
            favoriteService.favoriteList.indexAt(
              data.treeInstruction?.type === 'reorder-above'
                ? 'before'
                : 'after',
              favorite
            )
          );
          track.$.navigationPanel.organize.orderOrganizeItem({
            type: data.source.data.entity.type,
          });
        } else {
          return; // not supported
        }
      }
    },
    [favoriteService, setFavoriteSortMode]
  );
  const canDropOnFavoritesArea =
    favoriteSortMode !== 'manual' || visibleFavoriteItems.length === 0;

  const { dropTargetRef, draggedOverDraggable, draggedOverPosition } =
    useDropTarget<AffineDNDData>(
      () => ({
        data: {
          at: 'navigation-panel:favorite:root',
        },
        onDrop: handleDrop,
        canDrop: args =>
          canDropOnFavoritesArea &&
          ((typeof favoriteRootCanDrop === 'function'
            ? favoriteRootCanDrop(args)
            : favoriteRootCanDrop) ??
            false),
        allowExternal: true,
      }),
      [canDropOnFavoritesArea, handleDrop]
    );
  const handleFavoriteChildrenCanDrop = useMemo<
    DropTargetOptions<AffineDNDData>['canDrop']
  >(
    () => args => {
      if (
        favoriteSortMode !== 'manual' &&
        (args.treeInstruction?.type === 'reorder-above' ||
          args.treeInstruction?.type === 'reorder-below')
      ) {
        return false;
      }
      return (
        (typeof favoriteChildrenCanDrop === 'function'
          ? favoriteChildrenCanDrop(args)
          : favoriteChildrenCanDrop) ?? false
      );
    },
    [favoriteSortMode]
  );

  const favoriteTree = (
    <NavigationPanelTreeRoot
      placeholder={<RootEmpty onDrop={handleDrop} isLoading={isLoading} />}
    >
      {visibleFavoriteItems.map(({ favorite, key }) => (
        <NavigationPanelFavoriteNode
          key={key}
          favorite={favorite}
          onDrop={handleOnChildrenDrop}
          canDrop={handleFavoriteChildrenCanDrop}
          parentPath={path}
        />
      ))}
    </NavigationPanelTreeRoot>
  );

  if (presentation === 'panel') {
    const panel = (
      <div
        ref={dropTargetRef}
        className={panelStyles.panel}
        data-testid="navigation-panel-favorites"
        data-empty={visibleFavoriteItems.length === 0 || undefined}
      >
        {favoriteTree}
        {draggedOverDraggable && (
          <DropEffect
            position={draggedOverPosition}
            dropEffect={favoriteRootDropEffect({
              source: draggedOverDraggable,
              treeInstruction: null,
            })}
          />
        )}
      </div>
    );
    return navigationControl ? (
      <NavigationTreeSectionOwner
        path={path}
        label={t['com.streamlain.rootAppSidebar.favorites']()}
        entries={navigationEntries}
        headerRef={navigationControl.triggerRef}
        headerSelector={navigationControl.triggerSelector}
        expanded={navigationControl.open}
        onExpand={navigationControl.onOpen}
      >
        {panel}
      </NavigationTreeSectionOwner>
    ) : (
      panel
    );
  }

  return (
    <CollapsibleSection
      navigationEntries={navigationEntries}
      path={path}
      title={t['com.streamlain.rootAppSidebar.favorites']()}
      headerRef={dropTargetRef}
      testId="navigation-panel-favorites"
      headerTestId="navigation-panel-favorite-category-divider"
      revealOnExpand={revealOnExpand}
      actions={
        <>
          <SidebarAreaHeaderActions
            create={{
              actions: buildSidebarCreateActions({
                document: {
                  label:
                    t[
                      'com.streamlain.rootAppSidebar.organize.create.document'
                    ](),
                  onSelect: handleCreateNewFavoriteDoc,
                },
              }),
              testId: 'navigation-panel-bar-add-favorite-button',
              tooltip:
                t[
                  'com.streamlain.rootAppSidebar.explorer.fav-section-add-tooltip'
                ](),
            }}
            options={{
              sortMode: favoriteSortMode,
              onSortModeChange: setFavoriteSortMode,
              testId: 'navigation-panel-favorites-options-button',
            }}
          />
          {draggedOverDraggable && (
            <DropEffect
              position={draggedOverPosition}
              dropEffect={favoriteRootDropEffect({
                source: draggedOverDraggable,
                treeInstruction: null,
              })}
            />
          )}
        </>
      }
    >
      {favoriteTree}
    </CollapsibleSection>
  );
};

const childLocation = {
  at: 'navigation-panel:favorite:list' as const,
};

const FavoritePendingRemovalButton = ({
  id,
  type,
}: {
  id: string;
  type: FavoriteSupportTypeUnion;
}) => {
  const favoriteService = useService(FavoriteService);
  const t = useI18n();
  const pendingRemoval$ = useMemo(
    () => favoriteService.pendingRemoval$(type, id),
    [favoriteService, id, type]
  );
  const pendingRemoval = useLiveData(pendingRemoval$);
  const starRef = useRef<HTMLSpanElement>(null);

  const handleToggle = useCallback(() => {
    if (starRef.current)
      streamLainAnimationController.play(
        starRef.current,
        [
          { transform: 'scale(1)' },
          { transform: 'scale(1.12)' },
          { transform: 'scale(1)' },
        ],
        'feedback'
      );
    favoriteService.togglePendingRemoval(type, id);
  }, [favoriteService, id, type]);

  return (
    <IconButton
      {...KEEP_ACTION_REVEAL_ACTIVE_PROPS}
      aria-label={
        pendingRemoval
          ? t['Undo']()
          : t['com.streamlain.favoritePageOperation.remove']()
      }
      data-pending-removal={pendingRemoval ? 'true' : undefined}
      data-testid={`navigation-panel-favorite-toggle-${type}-${id}`}
      onClick={handleToggle}
      size="16"
    >
      <span ref={starRef} className={pendingRemovalStyles.root}>
        <FavoritedIcon className={pendingRemovalStyles.icon} />
        {pendingRemoval ? (
          <FavoritedIcon
            aria-hidden="true"
            className={clsx(
              pendingRemovalStyles.icon,
              pendingRemovalStyles.wipe
            )}
          />
        ) : null}
      </span>
    </IconButton>
  );
};

const NavigationPanelFavoriteNode = ({
  favorite,
  onDrop,
  canDrop,
  parentPath,
}: {
  favorite: {
    id: string;
    type: FavoriteSupportTypeUnion;
  };
  parentPath: string[];
  onDrop: (
    favorite: {
      id: string;
      type: FavoriteSupportTypeUnion;
    },
    data: DropTargetDropEvent<AffineDNDData>
  ) => void;
  canDrop?: DropTargetOptions<AffineDNDData>['canDrop'];
}) => {
  const quickToggleOperations = useMemo<NodeOperation[]>(
    () => [
      {
        id: 'favorite-quick-toggle',
        index: 190,
        inline: true,
        view: (
          <FavoritePendingRemovalButton id={favorite.id} type={favorite.type} />
        ),
      },
    ],
    [favorite.id, favorite.type]
  );
  const handleOnChildrenDrop = useCallback(
    (data: DropTargetDropEvent<AffineDNDData>) => {
      onDrop(favorite, data);
    },
    [favorite, onDrop]
  );
  const handleCanDrop = useMemo<DropTargetOptions<AffineDNDData>['canDrop']>(
    () => args => {
      const sourceType = args.source.data.entity?.type;
      const instructionType = args.treeInstruction?.type;
      const fromFavoriteList =
        args.source.data.from?.at === 'navigation-panel:favorite:list';

      if (
        favorite.type === 'doc' &&
        sourceType === 'doc' &&
        (instructionType === 'make-child' || !fromFavoriteList)
      ) {
        return false;
      }

      return (typeof canDrop === 'function' ? canDrop(args) : canDrop) ?? false;
    },
    [canDrop, favorite.type]
  );
  const dropEffect: NavigationPanelTreeNodeDropEffect =
    favorite.type === 'doc'
      ? data => {
          const sourceType = data.source.data.entity?.type;
          const instructionType = data.treeInstruction?.type;
          const fromFavoriteList =
            data.source.data.from?.at === 'navigation-panel:favorite:list';
          if (
            sourceType === 'doc' &&
            (instructionType === 'make-child' || !fromFavoriteList)
          ) {
            return;
          }
          return favoriteChildrenDropEffect(data);
        }
      : favoriteChildrenDropEffect;
  return favorite.type === 'doc' ? (
    <NavigationPanelDocNode
      key={favorite.id}
      docId={favorite.id}
      location={childLocation}
      onDrop={handleOnChildrenDrop}
      dropEffect={dropEffect}
      canDrop={handleCanDrop}
      operations={quickToggleOperations}
      parentPath={parentPath}
    />
  ) : favorite.type === 'tag' ? (
    <NavigationPanelTagNode
      key={favorite.id}
      tagId={favorite.id}
      location={childLocation}
      onDrop={handleOnChildrenDrop}
      dropEffect={favoriteChildrenDropEffect}
      canDrop={handleCanDrop}
      operations={quickToggleOperations}
      parentPath={parentPath}
    />
  ) : favorite.type === 'folder' ? (
    <NavigationPanelFolderNode
      key={favorite.id}
      nodeId={favorite.id}
      location={childLocation}
      onDrop={handleOnChildrenDrop}
      dropEffect={favoriteChildrenDropEffect}
      canDrop={handleCanDrop}
      operations={quickToggleOperations}
      parentPath={parentPath}
    />
  ) : (
    <NavigationPanelCollectionNode
      key={favorite.id}
      collectionId={favorite.id}
      location={childLocation}
      onDrop={handleOnChildrenDrop}
      dropEffect={favoriteChildrenDropEffect}
      canDrop={handleCanDrop}
      operations={quickToggleOperations}
      parentPath={parentPath}
    />
  );
};
